/**
 * RH-03 Paragraph uniformity (INV-CH-RH-03, ENGINE-SPEC §3.5, §4; changes C-RH-POOL, C-RH03-DAMP).
 *
 * Pooled within-joint-segment CV of prose paragraph length (own-block word count), with the same
 * estimator as RH-01: sqrt(Σ SS_s / Σ(n_s − 1)) / pooled mean · (1 + 1/(4N)), N = paragraphs.
 * The resampling items are the paragraphs themselves.
 */
import { derivePrior } from './framework.ts';
import {
	type DispItems,
	fmt,
	paragraphItems,
	pooledCv,
	proseParagraphs,
	runDispersion,
} from './helpers.ts';
import type { ChannelDef, ChannelImpl, Ramp, RunContext } from './types.ts';

const RAMP: Ramp = [
	[0.1, 88],
	[0.2, 72],
	[0.35, 52],
	[0.5, 34],
	[0.75, 16],
];

export const rh03Def: ChannelDef = {
	id: 'RH-03',
	name: 'Paragraph uniformity',
	family: 'RHYTHM',
	axis: 'authorship',
	registers: ['prose'],
	direction: -1,
	oneWay: false,
	estimator: 'distributional',
	features: [{ id: 'cv', label: 'Pooled within-segment CV of paragraph length' }],
	minExposure: 3,
	exposureUnit: 'paragraphs',
	lang: 'agnostic',
	ramp: RAMP,
	unitScale: 1,
	unitLabel: 'CV of paragraph length',
	prior: derivePrior(RAMP, 1, 'paragraphs', 1.2),
	weight: 0.9,
	nd: 1,
	changes: ['C-RH-POOL', 'C-RH03-DAMP'],
};

function run(ctx: RunContext) {
	const doc = ctx.doc;
	const ps = proseParagraphs(doc);
	let s1 = 0;
	let s2 = 0;
	for (const p of ps) {
		const w = doc.paragraphs.ownWords[p] as number;
		s1 += w;
		s2 += w * w;
	}
	const N = ps.length;
	const meanAll = N ? s1 / N : 0;
	const cvWhole = meanAll > 0 ? Math.sqrt(Math.max(0, s2 / N - meanAll ** 2)) / meanAll : 0;
	return runDispersion(ctx, {
		def: rh03Def,
		salt: 0x52480003,
		analyticFactor: 1,
		build: (segOf): { items: DispItems; exposure: number } => ({
			items: paragraphItems(doc, segOf),
			exposure: N,
		}),
		stat: pooledCv,
		extra: () => ({
			features: { cvWhole, meanParagraphLength: meanAll },
			summary: N ? [`${N} paragraphs · mean ${fmt(meanAll, 0)}w`] : [],
		}),
	});
}

export const rh03: ChannelImpl = { def: rh03Def, phase: 'derived', run };
